import React, { useState } from 'react';
import { Container, Button, Grid, Box, Typography } from '@mui/material';
import { AcceptTerms, Login } from '../../../api';

const Terms = ({ email, password, setAuthenticated, onAccept }) => {
  const [submitError, setSubmitError] = useState(false);

  const handleAccept = async () => {
    
    if (onAccept) {
      // Account creation flow - just call callback
      onAccept();
    } else {
      // Login flow - call API then login
      try {
        await AcceptTerms(email);
        if (password) {
          await Login(email, password);
          setAuthenticated(true);
        } else {
          setAuthenticated(true);
        }
      } catch (e) {
        const error = (e.response && e.response.data && e.response.data.errors) || e.message;
        setSubmitError(error);
      }
    }
  };

  return (
    <Container maxWidth="md">
      <Typography component="h1" variant="h4" gutterBottom>
        Terms & Conditions
      </Typography>
      {submitError && <Typography component="p" color="error">{submitError}</Typography>}
      
      <Box sx={{ maxHeight: '60vh', overflow: 'auto', border: '1px solid #ccc', padding: 2, marginY: 2 }}>
        <Typography variant="h5" gutterBottom align="center">
          CorridorVet – Terms & Conditions
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          1. Introduction
        </Typography>
        <Typography paragraph>
          Welcome to CorridorVet. These Terms & Conditions ("Terms") govern your access to and use of the CorridorVet website, platform, applications, services, content, and related tools ("Services"). By accessing or using the Services, you agree to be bound by these Terms.
        </Typography>
        <Typography paragraph>
          If you do not agree with these Terms, you may not use the Services.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          2. Definitions
        </Typography>
        <Typography paragraph>
          "CorridorVet," "we," "our," or "us" refers to CorridorVet and its affiliates.<br/>
          "User," "you," or "your" refers to any individual or entity accessing or using the Services.<br/>
          "Platform" refers to the CorridorVet web and software-based systems, including PACS, reporting tools, consult submission interfaces, and modality worklist features.<br/>
          "Content" refers to text, images, DICOM files, communications, reports, and any materials transmitted or stored within the Platform.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          3. Eligibility & Authorized Use
        </Typography>
        <Typography paragraph>
          You agree to use the Services only for lawful veterinary medical, diagnostic, administrative, or educational purposes.
        </Typography>
        <Typography paragraph>
          You represent that:
        </Typography>
        <Typography component="ol" sx={{ paddingLeft: 2, marginBottom: 2 }}>
          <li>You are authorized by your clinic or institution to use CorridorVet.</li>
          <li>All submitted cases and data are accurate and compliant with applicable laws.</li>
          <li>You will not use the Platform in a manner that impairs functionality, security, or access for other users.</li>
        </Typography>
        <Typography paragraph>
          Unauthorized access or use is strictly prohibited.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          4. Account Creation & Security
        </Typography>
        <Typography paragraph>
          Users must maintain accurate account information and protect login credentials. You agree to:
        </Typography>
        <Typography component="ul" sx={{ paddingLeft: 2, marginBottom: 2 }}>
          <li>Keep your password confidential.</li>
          <li>Notify CorridorVet promptly of any unauthorized access.</li>
          <li>Accept responsibility for all activity under your account.</li>
        </Typography>
        <Typography paragraph>
          CorridorVet may suspend or terminate accounts that violate these Terms.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          5. Use of Platform Features
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          5.1 Case Submission
        </Typography>
        <Typography paragraph>
          Users agree to:
        </Typography>
        <Typography component="ul" sx={{ paddingLeft: 2, marginBottom: 2 }}>
          <li>Submit complete and accurate case information.</li>
          <li>Ensure all images meet minimum diagnostic standards.</li>
          <li>Follow clinic-specific or CorridorVet-specific submission protocols.</li>
        </Typography>
        
        <Typography variant="subtitle1" gutterBottom>
          5.2 Modality Worklist
        </Typography>
        <Typography paragraph>
          Correct networking configuration is required for modality worklist functionality. CorridorVet is not responsible for misconfigured third-party devices or hardware.
        </Typography>
        
        <Typography variant="subtitle1" gutterBottom>
          5.3 Reporting & Medical–Legal Disclaimer
        </Typography>
        <Typography paragraph>
          Specialists using the CorridorVet Platform provide interpretations based solely on the diagnostic images, clinical history, and information submitted at the time of the request. These interpretations are limited to the materials provided and are intended to assist—but not replace—the independent clinical judgment of the attending veterinarian.
        </Typography>
        <Typography paragraph>
          CorridorVet does not directly employ the specialists who provide interpretations through the Platform. All professional services are performed by specialists who are engaged and employed by CorridorVet clients such as specialist groups, specialty hospitals, or independent contracting companies. The professional obligations, service standards, liability limitations, and reporting practices of these specialists may be governed by, or superseded by, the terms and conditions of their respective employing or contracting organizations. CorridorVet does not supervise, direct, or control the medical judgment or clinical decision-making of these specialists.
        </Typography>
        
        <Typography variant="subtitle2" gutterBottom>
          No Medical Provider Relationship
        </Typography>
        <Typography paragraph>
          CorridorVet is not a medical provider and does not provide veterinary medical services. Use of the Platform does not create a veterinarian–client–patient relationship between CorridorVet and any animal, clinic, pet owner, or user. All diagnostic interpretations and professional opinions accessible through the Platform originate exclusively from specialists employed or engaged by CorridorVet's client organizations—not from CorridorVet.
        </Typography>
        <Typography paragraph>
          CorridorVet does not assume any duty of care to patients, pet owners, or clinics and is not responsible for validating, verifying, or overseeing the licensure, performance, or professional standards of specialists using the Platform. No report, communication, or information transmitted through the Platform constitutes veterinary medical advice from CorridorVet.
        </Typography>
        
        <Typography variant="subtitle2" gutterBottom>
          Attending Veterinarian Responsibilities
        </Typography>
        <Typography paragraph>
          The submitting veterinarian expressly acknowledges and agrees that they remain solely responsible for:
        </Typography>
        <Typography component="ul" sx={{ paddingLeft: 2, marginBottom: 2 }}>
          <li>Performing a complete physical examination of the patient</li>
          <li>Correlating imaging findings with clinical signs, laboratory data, and the patient's overall condition</li>
          <li>Establishing the final diagnosis and treatment plan</li>
          <li>Communicating all medical recommendations and decisions to the animal's owner</li>
        </Typography>
        <Typography paragraph>
          Interpretations may be limited, inconclusive, or affected by incomplete history, poor image quality, or technical limitations of the imaging modality.
        </Typography>
        
        <Typography variant="subtitle2" gutterBottom>
          Liability Limitation
        </Typography>
        <Typography paragraph>
          To the fullest extent permitted by law, CorridorVet shall not be liable for:
        </Typography>
        <Typography component="ul" sx={{ paddingLeft: 2, marginBottom: 2 }}>
          <li>Diagnostic, therapeutic, or treatment decisions made by the attending veterinarian</li>
          <li>Errors, omissions, recommendations, or professional performance of specialists using the Platform</li>
          <li>Adverse patient outcomes, complications, or mortality</li>
          <li>Inaccurate, incomplete, or poor-quality images or information submitted</li>
          <li>Delays, miscommunications, or failures arising from third-party systems, equipment, or the specialist's employing organization</li>
        </Typography>
        <Typography paragraph>
          CorridorVet is not responsible for whether follow-up imaging, referral, or advanced diagnostics recommended by specialists are pursued or for outcomes related to such decisions.
        </Typography>
        
        <Typography variant="subtitle1" gutterBottom>
          5.4 Electronic Communication (Email & Text Message Permissions)
        </Typography>
        <Typography paragraph>
          By using the CorridorVet Platform, you acknowledge and consent to electronic communications—including email, SMS/text messaging, and system notifications—for delivery of clinical, administrative, and essential service-related information.
        </Typography>
        <Typography paragraph>
          You authorize CorridorVet to:
        </Typography>
        <Typography component="ul" sx={{ paddingLeft: 2, marginBottom: 2 }}>
          <li>Send clinical reports, case updates, consultation alerts, and imaging findings.</li>
          <li>Send administrative communications including billing notices, vendor authorization confirmations, and account verification instructions.</li>
          <li>Deliver security alerts, password resets, workflow reminders, and platform updates.</li>
        </Typography>
        
        <Typography variant="subtitle2" gutterBottom>
          Mandatory Email Communication
        </Typography>
        <Typography paragraph>
          Email communication is required for the operation of the CorridorVet Platform.
        </Typography>
        <Typography component="ul" sx={{ paddingLeft: 2, marginBottom: 2 }}>
          <li>Users may not opt out of email communications.</li>
          <li>Email is essential for report delivery, account access, billing, and case workflow.</li>
          <li>Blocking or filtering CorridorVet emails may limit Platform functionality.</li>
        </Typography>
        
        <Typography variant="subtitle2" gutterBottom>
          SMS/Text Messaging
        </Typography>
        <Typography paragraph>
          SMS may be used for convenience notifications. Users may modify SMS preferences except where required for account security (e.g., two-factor authentication).
        </Typography>
        
        <Typography variant="subtitle2" gutterBottom>
          User Responsibilities
        </Typography>
        <Typography paragraph>
          Users agree to:
        </Typography>
        <Typography component="ul" sx={{ paddingLeft: 2, marginBottom: 2 }}>
          <li>Provide accurate, authorized email addresses and phone numbers.</li>
          <li>Maintain updated contact information.</li>
          <li>Ensure systems are configured to receive CorridorVet communications.</li>
        </Typography>
        <Typography paragraph>
          CorridorVet is not responsible for delays or delivery failures caused by carriers, spam filtering, or third-party systems.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          6. User Responsibilities
        </Typography>
        <Typography paragraph>
          Users agree not to:
        </Typography>
        <Typography component="ul" sx={{ paddingLeft: 2, marginBottom: 2 }}>
          <li>Upload malware, harmful code, or disruptive content.</li>
          <li>Share login credentials.</li>
          <li>Attempt to reverse engineer or copy the Platform.</li>
          <li>Submit defamatory, abusive, harmful, or illegal content.</li>
          <li>Use the Platform for human medical purposes.</li>
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          7. Data Ownership & Licensing
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          7.1 User Data
        </Typography>
        <Typography paragraph>
          Users retain ownership of all submitted images, medical records, and case materials. By using the Platform, users grant CorridorVet a license to:
        </Typography>
        <Typography component="ul" sx={{ paddingLeft: 2, marginBottom: 2 }}>
          <li>Store, transmit, and process submitted data</li>
          <li>Use data for reporting, consultation, training, and platform improvement</li>
          <li>De-identify data for analytics and quality assurance</li>
        </Typography>
        <Typography paragraph>
          CorridorVet does not sell identifiable user or patient data.
        </Typography>
        
        <Typography variant="subtitle1" gutterBottom>
          7.2 CorridorVet Content
        </Typography>
        <Typography paragraph>
          All proprietary software, documentation, design elements, and trademarks are owned by CorridorVet and may not be reproduced without written authorization.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          8. Billing, Fees, and Payments
        </Typography>
        <Typography paragraph>
          Users and clinics agree to:
        </Typography>
        <Typography component="ul" sx={{ paddingLeft: 2, marginBottom: 2 }}>
          <li>Maintain accurate billing information</li>
          <li>Pay applicable service or consultation fees</li>
          <li>Resolve invoice discrepancies promptly</li>
        </Typography>
        <Typography paragraph>
          CorridorVet may suspend access for overdue balances. ACH or credit card payments may require completion of vendor authorization documentation.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          9. Service Availability & System Updates
        </Typography>
        <Typography paragraph>
          CorridorVet strives for reliable and continuous service but does not guarantee uninterrupted access. CorridorVet may:
        </Typography>
        <Typography component="ul" sx={{ paddingLeft: 2, marginBottom: 2 }}>
          <li>Modify or update features</li>
          <li>Perform scheduled or emergency maintenance</li>
          <li>Discontinue features or services with reasonable notice</li>
        </Typography>
        <Typography paragraph>
          Users will be notified of significant changes when feasible.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          10. Limitations of Liability
        </Typography>
        <Typography paragraph>
          CorridorVet disclaims all warranties, express or implied, to the greatest extent permitted by law. CorridorVet is not liable for damages resulting from:
        </Typography>
        <Typography component="ul" sx={{ paddingLeft: 2, marginBottom: 2 }}>
          <li>User error</li>
          <li>Data inaccuracies</li>
          <li>Hardware or network failures</li>
          <li>Third-party integrations</li>
          <li>Clinical outcomes or treatment decisions</li>
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          11. Indemnification
        </Typography>
        <Typography paragraph>
          Users agree to indemnify and hold harmless CorridorVet and its affiliates from claims arising from:
        </Typography>
        <Typography component="ul" sx={{ paddingLeft: 2, marginBottom: 2 }}>
          <li>Use of the Platform</li>
          <li>Violations of these Terms</li>
          <li>Data submitted or actions taken under the user's account</li>
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          12. Termination
        </Typography>
        <Typography paragraph>
          CorridorVet may suspend or terminate access for:
        </Typography>
        <Typography component="ul" sx={{ paddingLeft: 2, marginBottom: 2 }}>
          <li>Breaches of these Terms</li>
          <li>Platform misuse</li>
          <li>Non-payment</li>
          <li>Security or legal concerns</li>
        </Typography>
        <Typography paragraph>
          Users may discontinue use at any time.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          13. Governing Law
        </Typography>
        <Typography paragraph>
          These Terms are governed by the laws of the State of Washington unless superseded by a separate agreement.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          14. Changes to Terms
        </Typography>
        <Typography paragraph>
          CorridorVet may update these Terms periodically. Continued use of the Platform constitutes acceptance of revised Terms.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          15. Contact Information
        </Typography>
        <Typography paragraph>
          For questions or support related to these Terms or the CorridorVet Platform:<br/>
          Email: <a href="mailto:contact@corridor.vet" style={{color: "inherit"}}>contact@corridor.vet</a>
        </Typography>
      </Box>
      
      <Grid container justifyContent="center" spacing={2}>
        {onAccept && (
          <Grid item>
            <Box mt={3}>
              <Button 
                variant="outlined" 
                onClick={() => onAccept(false)}
              >
                Back
              </Button>
            </Box>
          </Grid>
        )}
        <Grid item>
          <Box mt={3}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleAccept}
            >
              Accept Terms
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Terms;